import { computed } from 'vue'
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/vue-query'
import type { OrderDocumentFile } from '../../types/orderTypes'

/**
 * A document blob that is fetched only on demand (View / Download) and then
 * kept for the session: preview and download read the same cached blob, and
 * a second open makes no request. The observer never fetches by itself — it
 * only makes the cached file (and its size) reactive for the row.
 */
export function useLazyOrderFileQuery(
  queryKey: () => QueryKey,
  fetchFile: () => Promise<OrderDocumentFile>,
) {
  const queryClient = useQueryClient()

  const options = () => ({
    queryKey: queryKey(),
    queryFn: fetchFile,
    staleTime: Infinity,
    gcTime: Infinity,
    // Each attempt is a full server-side render / CDN hit; the preview offers
    // its own retry instead.
    retry: false,
  })

  const fileQuery = useQuery({
    queryKey: () => queryKey(),
    queryFn: fetchFile,
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  })

  /** The cached file, fetching it first if this is the first open. */
  const load = (): Promise<OrderDocumentFile> => queryClient.fetchQuery(options())

  return {
    file: computed(() => fileQuery.data.value ?? null),
    load,
  }
}
